import deepmerge from 'deepmerge';
import {getIsAgeRestricted} from '../../../common/getIsAgeRestricted';
import {t} from '../../../common/getTranslations';
import {requestContext} from '../../../common/requestContext';
import {Icon} from '../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../design-system/atoms/Icon/IconType.enum';
import {Text} from '../../../design-system/atoms/Text/Text';
import {NormalListViewItem} from '../../../design-system/organisms/NormalListViewItem/NormalListViewItem';
import {RightButtonType} from '../../../design-system/organisms/NormalListViewItem/RightButtonType.enum';
import {showSimpleDialog} from '../../../dialog/simpleDialogHandler';
import {HelixTodayGiftList, Maybe} from '../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../logging/aggregateAndSendLog';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {userActionStr} from '../../../logging/TrackEventObject.interface';
import {useLogin} from '../../../user/useLogin.hook';
import {useClickGiftButton} from '../hook/useClickGiftButton';

export function TodayGiftHelixListContainer({
  currentCategoryUid,
  currentCategoryName,
  currentSubCategoryName,
  currentSubCategoryUid,
  list,
  description,
}: {
  currentCategoryUid: string;
  currentCategoryName?: string;
  currentSubCategoryName?: string;
  currentSubCategoryUid?: string;
  list: HelixTodayGiftList[];
  description?: Maybe<string>;
}) {
  const categoryUid = currentCategoryUid;
  const subCategoryUid = currentSubCategoryUid;
  const {onClickGiftButton} = useClickGiftButton({
    categoryUid,
    subCategoryUid,
  });
  const {user} = useLogin();

  return (
    <div className="mx-11pxr mb-8pxr flex flex-col justify-center rounded-8pxr bg-static-etc-bg-helix-gift pt-10pxr pb-5pxr">
      <div className="flex flex-col justify-center space-y-13pxr">
        {list.map(({id, helixGiftTitle, list: itemList}, listWrapperIndex) => (
          <div key={id}>
            {helixGiftTitle && (
              <div className="mb-5pxr flex items-center px-11pxr">
                <Text className="font-small1-bold text-ellipsis text-theme-solid-100 line-clamp-1">
                  {helixGiftTitle}
                </Text>
                {listWrapperIndex === 0 && (
                  <button
                    className="ml-4px shrink-0"
                    onClick={() => {
                      showSimpleDialog({
                        isMobile: requestContext.requestInfo.display.isMobile,
                        title: '',
                        description: description ?? t('giftbox_helix_info'),
                        okButton: {
                          text: t('common_confirm_button'),
                          type: 'label',
                          closeAfterClick: true,
                        },
                      });
                    }}
                  >
                    <Icon type={IconType.ButtonHelpS2} />
                  </button>
                )}
              </div>
            )}
            {itemList.map((item, index) => (
              <NormalListViewItem
                key={item.id}
                item={item}
                // TODO: 선물함 Legacy 컴포넌트들 모두 제거 시 NormalListViewItem 내부로 스타일 넣어주기
                className="px-11pxr"
                isAgeRestricted={getIsAgeRestricted({
                  age: user?.age,
                  authenticatedAt: user?.authenticatedAt,
                  ageGrade: item.ageGrade,
                  selfCensorship: item.selfCensorship,
                })}
                imageLazyLoad={true}
                rightButton={{
                  type: RightButtonType.Gift,
                  isActive: item.isReceived,
                  rounded: true,
                }}
                onClickRightButton={e => {
                  const giftItemLogObj = {
                    customProps: {
                      user_action: 'click' as userActionStr,
                    },
                  };
                  if (!item.isReceived) {
                    e.preventDefault();
                    e.stopPropagation();
                    aggregateAndSendLog({
                      targetElem: e.target as HTMLElement,
                      actionType: 'Event',
                      actionName: '선물받기_클릭',
                      ...(item.giftEventLog
                        ? deepmerge(item.giftEventLog, giftItemLogObj)
                        : giftItemLogObj),
                    });
                    onClickGiftButton({
                      ticketUid: item.ticketUid,
                      title: item.row1,
                      ageGrade: item.ageGrade,
                      seriesId: item.seriesId,
                      isHelixGift: true,
                    });
                  }
                }}
                onClick={e => {
                  const todayGiftItemLogObj = {
                    customProps: {
                      user_action: 'click' as userActionStr,
                    },
                  };
                  aggregateAndSendLog({
                    targetElem: e.target as HTMLElement,
                    actionType: 'Event',
                    actionName: '작품_클릭',
                    ...(item.eventLog
                      ? deepmerge(item.eventLog, todayGiftItemLogObj)
                      : todayGiftItemLogObj),
                  });
                }}
                onClickContinueView={e => {
                  const todayGiftItemLogObj = {
                    customProps: {
                      user_action: 'click' as userActionStr,
                    },
                  };
                  aggregateAndSendLog({
                    targetElem: e.target as HTMLElement,
                    actionType: 'Event',
                    actionName: '작품_클릭',
                    ...(item.eventLog
                      ? deepmerge(item.eventLog, todayGiftItemLogObj)
                      : todayGiftItemLogObj),
                  });
                }}
                {...putTiaraObjectProps({
                  click: {
                    layer1: 'Helix선물영역',
                    layer2: String(listWrapperIndex + 1),
                    ordnum: String(index + 1),
                  },
                  customProps: {
                    page_category_filter: currentCategoryName,
                    page_genre_filter: currentSubCategoryName,
                    helix_yn: 'y',
                    helix_id: String(item.ticketUid),
                  },
                })}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
